import type {
  SignatureCaptureHooks,
  SignatureCapturePort,
  SignatureCaptureSession,
} from '../../domain/ports/SignatureCapturePort.ts';
import { imageDataHasSignatureInk } from '../../domain/signatureInk.ts';

export function canvasHasInk(canvas: HTMLCanvasElement): boolean {
  const ctx = canvas.getContext('2d');
  if (!ctx) return false;
  return imageDataHasSignatureInk(ctx.getImageData(0, 0, canvas.width, canvas.height).data);
}

/** Normalizes signature PNG to white background; remaps legacy near-black stroke pixels. */
export async function flattenSignatureBlob(blob: Blob): Promise<Blob> {
  const bitmap = await createImageBitmap(blob);
  const canvas = document.createElement('canvas');
  canvas.width = bitmap.width;
  canvas.height = bitmap.height;
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    bitmap.close();
    return blob;
  }
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.drawImage(bitmap, 0, 0);
  bitmap.close();
  const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
  const { data } = imageData;
  for (let i = 0; i < data.length; i += 4) {
    const r = data[i]!;
    const g = data[i + 1]!;
    const b = data[i + 2]!;
    if (data[i + 3]! > 0 && r <= 16 && g <= 16 && b <= 16) {
      data[i] = 255;
      data[i + 1] = 255;
      data[i + 2] = 255;
      data[i + 3] = 255;
    }
  }
  ctx.putImageData(imageData, 0, 0);
  return new Promise((resolve) => {
    canvas.toBlob((next) => resolve(next ?? blob), 'image/png');
  });
}

async function canvasToPngBlob(canvas: HTMLCanvasElement): Promise<Blob | null> {
  const flat = document.createElement('canvas');
  flat.width = canvas.width;
  flat.height = canvas.height;
  const ctx = flat.getContext('2d');
  if (!ctx) return null;
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, flat.width, flat.height);
  ctx.drawImage(canvas, 0, 0);
  return new Promise((resolve) => flat.toBlob((b) => resolve(b), 'image/png'));
}

function createSignatureCaptureSession(hooks: SignatureCaptureHooks): SignatureCaptureSession {
  let canvas: HTMLCanvasElement | null = null;
  let drawing = false;

  async function restoreSignatureOnCanvas(target: HTMLCanvasElement): Promise<void> {
    const ctx = target.getContext('2d');
    if (!ctx) return;
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, target.width, target.height);
    const blob = hooks.getFirmaBlob();
    if (!blob) return;
    try {
      const bitmap = await createImageBitmap(blob);
      ctx.drawImage(bitmap, 0, 0, target.width, target.height);
      bitmap.close();
    } catch {
      hooks.onFirmaRestoreFailed?.();
    }
  }

  return {
    bindCanvas(target: HTMLCanvasElement | undefined) {
      if (!target) return;
      const firstBind = target !== canvas;
      canvas = target;
      const ctx = target.getContext('2d');
      if (!ctx) return;
      void restoreSignatureOnCanvas(target).then(() => {
        ctx.strokeStyle = '#111';
        ctx.lineWidth = 2;
        ctx.lineCap = 'round';
      });
      if (!firstBind) return;

      const pos = (e: PointerEvent) => {
        const rect = target.getBoundingClientRect();
        const scaleX = target.width / rect.width;
        const scaleY = target.height / rect.height;
        return { x: (e.clientX - rect.left) * scaleX, y: (e.clientY - rect.top) * scaleY };
      };

      target.onpointerdown = (e) => {
        drawing = true;
        target.setPointerCapture(e.pointerId);
        const pt = pos(e);
        ctx.beginPath();
        ctx.moveTo(pt.x, pt.y);
      };
      target.onpointermove = (e) => {
        if (!drawing) return;
        const pt = pos(e);
        ctx.lineTo(pt.x, pt.y);
        ctx.stroke();
      };
      const stop = () => {
        drawing = false;
      };
      target.onpointerup = async () => {
        stop();
        if (canvasHasInk(target)) {
          const blob = await canvasToPngBlob(target);
          if (blob) {
            hooks.setFirmaBlob(blob);
            hooks.onCaptured();
          }
        }
      };
      target.onpointerleave = stop;
      // iOS Safari emite pointercancel en gestos de sistema (swipe borde, multitouch)
      target.onpointercancel = stop;
    },
    refreshCanvas() {
      if (canvas) void restoreSignatureOnCanvas(canvas);
    },
    clear() {
      if (canvas) {
        const ctx = canvas.getContext('2d');
        if (ctx) {
          ctx.fillStyle = '#ffffff';
          ctx.fillRect(0, 0, canvas.width, canvas.height);
        }
      }
      hooks.setFirmaBlob(null);
    },
    async capture() {
      if (!hooks.getFirmaBlob() && canvas && canvasHasInk(canvas)) {
        const blob = await canvasToPngBlob(canvas);
        if (blob) hooks.setFirmaBlob(blob);
      }
      return hooks.getFirmaBlob();
    },
  };
}

export class BrowserSignatureCaptureAdapter implements SignatureCapturePort {
  createSession(hooks: SignatureCaptureHooks): SignatureCaptureSession {
    return createSignatureCaptureSession(hooks);
  }

  flattenSignatureBlob(blob: Blob): Promise<Blob> {
    return flattenSignatureBlob(blob);
  }
}
